import { UnknownAction } from '@reduxjs/toolkit'
import { createReducer } from '@reduxjs/toolkit/react'
import { Traceable } from '@repo/shared/dtos/assistant'
import { BeneficiaryStatus } from '@repo/shared/dtos/beneficiary'
import { TimeType } from '@repo/shared/dtos/generic'
import { StructuredDataValueDTO } from '@repo/shared/dtos/structured-data'
import { AsyncData, DataStatus } from '@repo/ui/utils'

import { adminStoreActions, storeActions } from '@/store/actions'

export type Beneficiary = {
  caseWorkerIds: string[]
  createdAt: TimeType
  fullName: string
  id: string
  profile: { value: string } & Traceable
  status: BeneficiaryStatus
  updatedAt: TimeType
  structuredData: { value: StructuredDataValueDTO } & Traceable
}

export type BeneficiaryState = {
  currentBeneficiaryId?: Beneficiary['id']
  all: AsyncData<Beneficiary[]>
}

export const beneficiaryInitialState: BeneficiaryState = {
  currentBeneficiaryId: undefined,
  all: DataStatus.Uninitialized,
}

const addBeneficiaries = (state: BeneficiaryState, beneficiaries: Beneficiary[]) => {
  if (DataStatus.isUninitialized(state.all)) state.all = []
  beneficiaries.forEach((beneficiary) => {
    if (DataStatus.isFulfilled(state.all)) {
      const isFound = state.all.some((v) => v.id === beneficiary.id)
      if (!isFound) state.all.push(beneficiary)
    }
  })
}

const updateBeneficiaries = (state: BeneficiaryState, beneficiaries: Beneficiary[]) => {
  if (!DataStatus.isFulfilled(state.all)) return
  state.all = state.all.map((beneficiary) => {
    const updatedBeneficiary = beneficiaries.find(({ id }) => id === beneficiary.id)
    return updatedBeneficiary ? updatedBeneficiary : beneficiary
  })
}

export const beneficiaryReducer: ((
  state: BeneficiaryState | undefined,
  action: UnknownAction,
) => BeneficiaryState) & {
  getInitialState: () => BeneficiaryState
} = createReducer(beneficiaryInitialState, (builder) => {
  builder.addCase(storeActions.cleanUpOrganization, () => {
    return beneficiaryInitialState
  })

  builder.addCase(adminStoreActions.initAdmin.pending, () => beneficiaryInitialState)

  builder
    .addCase(
      storeActions.initOrganization.pending,
      (
        state,
        {
          meta: {
            arg: { isSameOrganization },
          },
        },
      ) => {
        if (isSameOrganization) return
        state.all = DataStatus.Loading
      },
    )
    .addCase(storeActions.initOrganization.rejected, (state) => {
      state.all = DataStatus.Error
    })
    .addCase(storeActions.initOrganization.fulfilled, (state, { payload: { beneficiaries } }) => {
      state.all = beneficiaries
    })

  builder.addCase(storeActions.initDashboard, (state) => {
    state.currentBeneficiaryId = undefined
  })

  builder.addCase(
    storeActions.beneficiary.delete.fulfilled,
    (
      state,
      {
        meta: {
          arg: { beneficiaryId },
        },
        payload: { isDeleted },
      },
    ) => {
      if (!isDeleted) return
      state.currentBeneficiaryId = undefined
      if (!DataStatus.isFulfilled(state.all)) return
      state.all = state.all.filter((b) => b.id !== beneficiaryId)
    },
  )

  builder.addCase(
    storeActions.beneficiary.initBeneficiary.pending,
    (
      state,
      {
        meta: {
          arg: { beneficiaryId, isSameBeneficiary },
        },
      },
    ) => {
      if (!DataStatus.isFulfilled(state.all)) return
      if (isSameBeneficiary) return
      state.currentBeneficiaryId = beneficiaryId
    },
  )

  builder.addCase(
    storeActions.beneficiary.initBeneficiary.fulfilled,
    (state, { payload: { currentBeneficiary } }) => {
      addBeneficiaries(state, [currentBeneficiary])
    },
  )

  builder.addCase(
    storeActions.beneficiary.create.fulfilled,
    (state, { payload: { beneficiary } }) => {
      addBeneficiaries(state, [beneficiary])
    },
  )

  builder.addCase(
    storeActions.beneficiary.update.fulfilled,
    (state, { payload: { beneficiary } }) => {
      updateBeneficiaries(state, [beneficiary])
    },
  )

  builder.addCase(
    storeActions.beneficiary.refreshProfile.fulfilled,
    (state, { payload: { beneficiary } }) => {
      updateBeneficiaries(state, [beneficiary])
    },
  )

  builder.addCase(
    storeActions.beneficiary.refreshStructuredData.fulfilled,
    (state, { payload: { beneficiary } }) => {
      updateBeneficiaries(state, [beneficiary])
    },
  )
})
